import Phaser from 'phaser';
import { audioManager } from '@/systems/AudioManager';
import { QuestionEngine } from '@/systems/QuestionEngine';
import { ScoreSystem } from '@/systems/ScoreSystem';
import { saveSystem } from '@/systems/SaveSystem';
import type { PerTableStats, Question } from '@/types';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { ComboMeter } from '@/ui/components/ComboMeter';
import { Layout, Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';
import type { TimeAttackResultPayload } from '@/scenes/TimeAttackResultScene';

const TOTAL_DURATION_MS = 60000;
const PER_QUESTION_MS = 4000;
const FEEDBACK_DELAY_MS = 350;
const TIMER_BAR_WIDTH = 600;
const TIMER_BAR_HEIGHT = 14;
const COUNTDOWN_TICK_THRESHOLD_MS = 5000;

/**
 * TimeAttackScene — modo "Contra o Tempo" (CLAUDE.md §4.2).
 *
 * Diferenças do modo Missão:
 *   - 60s globais correndo, sem vidas. Tempo zero termina a sessão.
 *   - Cada conta tem 4s individuais; expirar zera combo (sem outra penalidade).
 *   - Pool é todas as tabuadas (1..12), pra ser desafio "geral".
 *   - Recorde pessoal exibido no HUD; bate no fim → animação especial.
 *
 * Feedback acelerado (350ms entre contas) pra encaixar o máximo de
 * questões em 60s.
 */
export class TimeAttackScene extends Phaser.Scene {
  private engine!: QuestionEngine;
  private scoreSys!: ScoreSystem;
  private currentQuestion?: Question;

  private maxCombo = 0;
  private totalCorrectResponseTimeMs = 0;
  private perTable: Record<number, PerTableStats> = {};
  private sessionStartedAt = 0;
  private questionStartedAt = 0;
  private remainingMs = TOTAL_DURATION_MS;
  private lastTickPlayedSecond = -1;

  // HUD
  private hudScore?: Phaser.GameObjects.Text;
  private hudRecord?: Phaser.GameObjects.Text;
  private hudCountdown?: Phaser.GameObjects.Text;
  private comboMeter?: ComboMeter;

  // Question UI
  private questionLabel?: Phaser.GameObjects.Text;
  private feedbackLabel?: Phaser.GameObjects.Text;
  private timerFill?: Phaser.GameObjects.Graphics;
  private perQuestionTimerEvent?: Phaser.Time.TimerEvent;
  private globalTickEvent?: Phaser.Time.TimerEvent;

  private answerButtons: Button[] = [];
  private acceptingInput = false;
  private finished = false;

  constructor() {
    super({ key: 'TimeAttackScene' });
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    this.engine = new QuestionEngine({
      level: 1,
      tables: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      timeLimitMs: PER_QUESTION_MS,
    });
    this.scoreSys = new ScoreSystem();
    this.maxCombo = 0;
    this.totalCorrectResponseTimeMs = 0;
    this.perTable = {};
    this.remainingMs = TOTAL_DURATION_MS;
    this.lastTickPlayedSecond = -1;
    this.finished = false;
    this.sessionStartedAt = this.time.now;

    this.buildHud();
    this.buildQuestionArea();
    this.buildAbortButton();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.cleanupPerQuestionTimer();
      this.globalTickEvent?.remove(false);
    });

    this.globalTickEvent = this.time.addEvent({
      delay: 100,
      loop: true,
      callback: () => this.onGlobalTick(),
    });

    this.nextQuestion();
  }

  private buildHud(): void {
    const { width } = this.scale;

    this.hudScore = this.add.text(32, 24, this.formatScore(), {
      fontFamily: Typography.fontFamily,
      fontSize: '28px',
      color: Palette.textPrimaryCss,
      fontStyle: 'bold',
    });

    this.hudRecord = this.add
      .text(width / 2, 32, this.formatRecord(), {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5, 0);

    // Countdown grande à direita — é a métrica mais importante visualmente.
    this.hudCountdown = this.add
      .text(width - 32, 32, this.formatCountdown(), {
        fontFamily: Typography.fontFamily,
        fontSize: '40px',
        color: Palette.accentCss,
        fontStyle: 'bold',
      })
      .setOrigin(1, 0);

    this.comboMeter = new ComboMeter(this, width - 96, 120);
  }

  private buildQuestionArea(): void {
    const { width, height } = this.scale;

    this.add
      .text(width / 2, height * 0.18, 'Contra o Tempo · 60s', {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.questionLabel = this.add
      .text(width / 2, height * 0.32, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '96px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const barX = width / 2 - TIMER_BAR_WIDTH / 2;
    const barY = height * 0.42;
    const frame = this.add.graphics();
    frame.lineStyle(2, Palette.textMuted, 1);
    frame.strokeRoundedRect(barX - 2, barY - 2, TIMER_BAR_WIDTH + 4, TIMER_BAR_HEIGHT + 4, 6);

    this.timerFill = this.add.graphics();

    this.feedbackLabel = this.add
      .text(width / 2, height * 0.5, '', {
        fontFamily: Typography.fontFamily,
        fontSize: '24px',
        color: Palette.successCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private buildAbortButton(): void {
    const { width, height } = this.scale;
    new Button(this, width - 110, height - 48, 'Desistir', {
      width: 180,
      height: 48,
      fontSize: 18,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.danger,
      onClick: () => this.endSession(),
    });
  }

  /* ---------------------------- loop principal --------------------------- */

  private nextQuestion(): void {
    if (this.finished) return;
    this.currentQuestion = this.engine.generate();
    this.feedbackLabel?.setText('');
    this.questionLabel?.setText(`${this.currentQuestion.factorA} × ${this.currentQuestion.factorB} = ?`);
    this.animateQuestionEntry();
    this.renderAnswerButtons(this.currentQuestion);
    this.startPerQuestionTimer();
    this.questionStartedAt = this.time.now;
    this.acceptingInput = true;
  }

  private renderAnswerButtons(question: Question): void {
    this.answerButtons.forEach((b) => b.destroy());
    this.answerButtons = [];

    const { width, height } = this.scale;
    const cols = 2;
    const buttonWidth = 220;
    const buttonHeight = 88;
    const gapX = 40;
    const gapY = 28;
    const totalWidth = cols * buttonWidth + (cols - 1) * gapX;
    const startX = width / 2 - totalWidth / 2 + buttonWidth / 2;
    const startY = height * 0.66;

    question.options.forEach((value, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      const x = startX + col * (buttonWidth + gapX);
      const y = startY + row * (buttonHeight + gapY);

      const btn = new Button(this, x, y, String(value), {
        width: buttonWidth,
        height: buttonHeight,
        fontSize: 40,
        fillColor: Palette.primary,
        hoverColor: Palette.accent,
        cornerRadius: Layout.cornerRadius,
        onClick: () => this.onAnswer(value, index),
      });
      this.answerButtons.push(btn);
    });
  }

  private startPerQuestionTimer(): void {
    this.cleanupPerQuestionTimer();
    this.drawPerQuestionTimer(1);
    this.perQuestionTimerEvent = this.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        const elapsed = this.time.now - this.questionStartedAt;
        const remaining = Math.max(0, 1 - elapsed / PER_QUESTION_MS);
        this.drawPerQuestionTimer(remaining);
        if (remaining <= 0) {
          this.cleanupPerQuestionTimer();
          this.onPerQuestionExpired();
        }
      },
    });
  }

  private drawPerQuestionTimer(ratio: number): void {
    if (!this.timerFill) return;
    const { width, height } = this.scale;
    const barX = width / 2 - TIMER_BAR_WIDTH / 2;
    const barY = height * 0.42;
    const color = ratio > 0.5 ? Palette.accent : ratio > 0.2 ? Palette.warning : Palette.danger;

    this.timerFill.clear();
    this.timerFill.fillStyle(color, 1);
    this.timerFill.fillRoundedRect(barX, barY, TIMER_BAR_WIDTH * ratio, TIMER_BAR_HEIGHT, 6);
  }

  private cleanupPerQuestionTimer(): void {
    this.perQuestionTimerEvent?.remove(false);
    this.perQuestionTimerEvent = undefined;
  }

  private onGlobalTick(): void {
    if (this.finished) return;
    this.remainingMs = Math.max(0, TOTAL_DURATION_MS - (this.time.now - this.sessionStartedAt));
    this.refreshCountdown();

    // Tick sonoro a cada segundo nos 5 últimos segundos.
    if (this.remainingMs <= COUNTDOWN_TICK_THRESHOLD_MS && this.remainingMs > 0) {
      const secondsLeft = Math.ceil(this.remainingMs / 1000);
      if (secondsLeft !== this.lastTickPlayedSecond) {
        this.lastTickPlayedSecond = secondsLeft;
        audioManager.playSfx('star', { starIndex: 0 }); // ping curto reusado
      }
    }

    if (this.remainingMs <= 0) {
      this.endSession();
    }
  }

  private onAnswer(value: number, buttonIndex: number): void {
    if (!this.acceptingInput || !this.currentQuestion || this.finished) return;
    this.acceptingInput = false;
    this.cleanupPerQuestionTimer();
    this.drawPerQuestionTimer(0);

    const responseTime = this.time.now - this.questionStartedAt;
    const correct = value === this.currentQuestion.correctAnswer;
    this.engine.recordResult(this.currentQuestion, correct);

    const clickedButton = this.answerButtons[buttonIndex];

    if (correct) {
      const result = this.scoreSys.recordCorrect(responseTime, PER_QUESTION_MS);
      this.maxCombo = Math.max(this.maxCombo, result.combo);
      this.totalCorrectResponseTimeMs += responseTime;
      const turbo = result.speedBonus ? ' · Turbo!' : '';
      this.feedbackLabel?.setColor(Palette.successCss).setText(`+${result.points}${turbo}`);
      audioManager.playSfx(result.speedBonus ? 'correctTurbo' : 'correct', { combo: result.combo });
      clickedButton?.flashCorrect();
      this.spawnScorePopup(clickedButton, result.points, result.speedBonus);
      if (result.combo > 0 && result.combo % 5 === 0) {
        audioManager.playSfx('combo', { combo: result.combo });
      }
    } else {
      this.scoreSys.recordWrong();
      this.feedbackLabel?.setColor(Palette.warningCss).setText('Quase!');
      audioManager.playSfx('wrong');
      clickedButton?.flashWrong();
      this.highlightCorrectButton();
    }

    this.trackPerTable(this.currentQuestion, correct);
    this.refreshHud();
    this.time.delayedCall(FEEDBACK_DELAY_MS, () => this.nextQuestion());
  }

  private onPerQuestionExpired(): void {
    if (!this.acceptingInput || !this.currentQuestion || this.finished) return;
    this.acceptingInput = false;

    this.engine.recordResult(this.currentQuestion, false);
    this.scoreSys.recordWrong();

    this.feedbackLabel?.setColor(Palette.textMutedCss).setText('Tempo!');
    audioManager.playSfx('timeUp');
    this.highlightCorrectButton();

    this.trackPerTable(this.currentQuestion, false);
    this.refreshHud();
    this.time.delayedCall(FEEDBACK_DELAY_MS, () => this.nextQuestion());
  }

  private endSession(): void {
    if (this.finished) return;
    this.finished = true;
    this.acceptingInput = false;
    this.cleanupPerQuestionTimer();
    this.globalTickEvent?.remove(false);
    this.answerButtons.forEach((b) => b.destroy());
    this.answerButtons = [];

    const snap = this.scoreSys.snapshot();
    const payload: TimeAttackResultPayload = {
      score: snap.score,
      correctCount: snap.correctCount,
      wrongCount: snap.wrongCount,
      speedBonusRate: snap.speedBonusRate,
      maxCombo: this.maxCombo,
      totalCorrectResponseTimeMs: this.totalCorrectResponseTimeMs,
      perTable: this.perTable,
      durationMs: TOTAL_DURATION_MS - this.remainingMs,
    };
    fadeToScene(this, 'TimeAttackResultScene', payload);
  }

  private trackPerTable(question: Question, correct: boolean): void {
    const inc = (table: number) => {
      const stats = this.perTable[table] ?? { questions: 0, correct: 0 };
      this.perTable[table] = {
        questions: stats.questions + 1,
        correct: stats.correct + (correct ? 1 : 0),
      };
    };
    inc(question.factorA);
    if (question.factorB !== question.factorA) inc(question.factorB);
  }

  private animateQuestionEntry(): void {
    if (!this.questionLabel) return;
    this.questionLabel.setScale(0.85);
    this.tweens.add({
      targets: this.questionLabel,
      scale: 1,
      duration: 220,
      ease: 'Back.easeOut',
    });
  }

  private highlightCorrectButton(): void {
    if (!this.currentQuestion) return;
    const correctIndex = this.currentQuestion.options.findIndex(
      (opt) => opt === this.currentQuestion!.correctAnswer,
    );
    if (correctIndex >= 0) this.answerButtons[correctIndex]?.highlightAsCorrect();
  }

  private spawnScorePopup(origin: Phaser.GameObjects.Container | undefined, points: number, turbo: boolean): void {
    if (!origin) return;
    const text = turbo ? `+${points} Turbo!` : `+${points}`;
    const popup = this.add
      .text(origin.x, origin.y - origin.height / 2, text, {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: turbo ? Palette.warningCss : Palette.successCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.tweens.add({
      targets: popup,
      y: popup.y - 60,
      alpha: { from: 1, to: 0 },
      duration: 700,
      ease: 'Cubic.easeOut',
      onComplete: () => popup.destroy(),
    });
  }

  /* ------------------------------ HUD ------------------------------ */

  private refreshHud(): void {
    this.hudScore?.setText(this.formatScore());
    this.hudRecord?.setText(this.formatRecord());
    this.comboMeter?.update(this.scoreSys.getCombo(), this.scoreSys.getMultiplier());
    this.refreshCountdown();
  }

  private refreshCountdown(): void {
    if (!this.hudCountdown) return;
    this.hudCountdown.setText(this.formatCountdown());
    const ratio = this.remainingMs / TOTAL_DURATION_MS;
    const color = ratio > 0.5 ? Palette.accentCss : ratio > 0.2 ? Palette.warningCss : Palette.dangerCss;
    this.hudCountdown.setColor(color);
  }

  private formatScore(): string {
    return `${this.scoreSys?.getScore() ?? 0} pts`;
  }

  private formatRecord(): string {
    const best = saveSystem.getData().timeAttackBestScore;
    return best > 0 ? `Recorde: ${best} pts` : 'Sem recorde ainda';
  }

  private formatCountdown(): string {
    const seconds = Math.max(0, Math.ceil(this.remainingMs / 1000));
    return `${seconds}s`;
  }
}
